const CANCELLED_STATUSES = new Set(['cancelled', 'cancelled_late']);
const JOINABLE_STATUSES = new Set(['scheduled', 'completed']);
const GENERIC_SPECIALIST_NAME = /specialist|ielts|toefl/i;

export const formatSpecialistShortName = (raw) => {
  const name = String(raw || '').replace(/\s+/g, ' ').trim();
  if (!name || GENERIC_SPECIALIST_NAME.test(name)) return '';
  const parts = name.split(' ').filter(Boolean);
  const first = parts[0].replace(/\.+$/, '');
  if (parts.length === 1) return first;
  const last = parts[parts.length - 1].replace(/\.+$/, '');
  if (!last) return first;
  return `${first} ${last[0].toUpperCase()}.`;
};

export const resolveSpecialistDisplayName = (eligibility, extraNames = []) => {
  const source = eligibility || {};
  const { upcoming, past } = splitSpecialistBookings(source.bookings || []);
  const candidates = [
    ...upcoming.map((booking) => booking.teacherName),
    source.specialistNote?.authorName,
    ...past.map((booking) => booking.teacherName),
    source.teacherName,
    ...(source.specialists || []).map((item) => item?.name || item),
    ...extraNames,
  ];
  for (const candidate of candidates) {
    const shortName = formatSpecialistShortName(candidate);
    if (shortName) return shortName;
  }
  return '';
};

export const formatSpecialistSlot = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const bookingEndMs = (booking) => {
  if (booking?.slotEnd) return new Date(booking.slotEnd).getTime();
  if (!booking?.slotStart) return 0;
  return new Date(booking.slotStart).getTime() + 45 * 60 * 1000;
};

export const isPastSpecialistBooking = (booking, now = Date.now()) => {
  if (!booking) return false;
  if (CANCELLED_STATUSES.has(booking.status) || booking.status === 'missed') {
    return true;
  }
  if (booking.status === 'completed') return true;
  return bookingEndMs(booking) < now;
};

export const splitSpecialistBookings = (bookings = [], now = Date.now()) => {
  const upcoming = [];
  const past = [];
  bookings.forEach((booking) => {
    if (!booking) return;
    if (isPastSpecialistBooking(booking, now)) {
      past.push(booking);
    } else {
      upcoming.push(booking);
    }
  });
  upcoming.sort((a, b) => new Date(a.slotStart) - new Date(b.slotStart));
  past.sort((a, b) => new Date(b.slotStart) - new Date(a.slotStart));
  return { upcoming, past };
};

export const specialistSessionPhase = (booking, now = Date.now()) => {
  if (!booking) return 'past';
  if (CANCELLED_STATUSES.has(booking.status) || booking.status === 'missed') {
    return 'past';
  }
  if (booking.status === 'completed') return 'past';
  const start = booking.slotStart ? new Date(booking.slotStart).getTime() : 0;
  const end = bookingEndMs(booking);
  if (now >= start - 15 * 60 * 1000 && now <= end + 15 * 60 * 1000) {
    return 'live';
  }
  if (end < now) return 'past';
  return 'upcoming';
};

export const canJoinSpecialistBooking = (booking, now = Date.now()) => {
  if (!booking?.meetLink) return false;
  if (!JOINABLE_STATUSES.has(booking.status)) return false;
  if (CANCELLED_STATUSES.has(booking.status) || booking.status === 'missed') {
    return false;
  }
  const phase = specialistSessionPhase(booking, now);
  return phase === 'upcoming' || phase === 'live';
};
